import React from "react";
import "./App.css";
import { Menu } from "lucide-react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { EntitlementsProvider } from "./context/EntitlementsContext";
import { Toaster } from "./components/ui/sonner";
import AuthCallback from "./components/AuthCallback";
import GoogleCallback from "./components/GoogleCallback";
import Sidebar from "./components/Sidebar";
import SupportWidget from "./components/SupportWidget";
import Gated from "./components/Gated";
import Onboarding from "./components/Onboarding";
import ConnectBroker from "./pages/ConnectBroker";
import Legal from "./pages/Legal";
import CommandCenter from "./pages/CommandCenter";
import AIJournal from "./pages/AIJournal";
import WSAIChartPage from "./pages/WSAIChartPage";
import WSAICoach from "./pages/WSAICoach";
import Analytics from "./pages/Analytics";
import WSNewsPro from "./pages/WSNewsPro";
import WSVIPCommunity from "./pages/WSVIPCommunity";
import Community from "./pages/Community";
import GrowthZone from "./pages/GrowthZone";
import WSLeaderboard from "./pages/WSLeaderboard";
import ComingSoon from "./pages/ComingSoon";
import GiveawayZone from "./pages/GiveawayZone";
import LandingPage from "./pages/LandingPage";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import AdminConsole from "./pages/AdminConsole";
import ReviewPopup from "./components/ReviewPopup";
import MaintenancePage from "./pages/MaintenancePage";

function AppShell() {
  const { user, login, logout } = useAuth();
  const [collapsed, setCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [maint, setMaint] = React.useState(null);
  const [bypass, setBypass] = React.useState(() => sessionStorage.getItem("ws_maint_bypass") === "1");
  const location = useLocation();
  React.useEffect(() => {
    document.documentElement.style.setProperty("--ws-sidebar-w", collapsed ? "68px" : "240px");
  }, [collapsed]);
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/maintenance`)
      .then((r) => r.json()).then((d) => setMaint(!!d.enabled)).catch(() => setMaint(false));
  }, []);
  // Maintenance overlay for EVERYONE (logged in or out). Admins get controls to exit/disable.
  if (maint === null) return <div className="min-h-screen" />;
  if (maint && !bypass) {
    return <MaintenancePage user={user} onLogin={login}
      onEnter={() => { sessionStorage.setItem("ws_maint_bypass", "1"); setBypass(true); }} />;
  }
  // Logged-out visitors get the marketing landing page; signing in unlocks the full app.
  if (!user) {
    return (
      <Routes>
        <Route path="/about" element={<AboutUs onGetStarted={login} />} />
        <Route path="/contact" element={<ContactUs onGetStarted={login} />} />
        <Route path="/disclaimer" element={<Legal doc="disclaimer" />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/terms" element={<Legal doc="terms" />} />
        <Route path="*" element={<LandingPage onGetStarted={login} />} />
      </Routes>
    );
  }
  return (
    <>
      {/* Mobile top bar (hamburger) — hidden on desktop */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 z-30 flex items-center gap-3 px-4 border-b border-[color:var(--ws-border)] backdrop-blur-md" style={{ background: "rgba(8,13,26,0.85)" }}>
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Open menu"
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white hover:bg-[rgba(255,255,255,0.08)] transition-colors">
          <Menu className="w-5 h-5" />
        </button>
        <span className="ws-logo-3d w-8 h-8"><img src="/wsj-logo.webp" alt="Wealth Secret" /></span>
        <span className="ws-heading text-white font-semibold text-[14px]">Wealth Secret</span>
      </div>
      {/* Drawer backdrop */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="mobile-backdrop" />
      )}
      <Sidebar user={user} onSignIn={login} onSignOut={logout} collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      <Onboarding />
      <ReviewPopup />
      <main className={`${collapsed ? "lg:ml-[68px]" : "lg:ml-[240px]"} ml-0 pt-14 lg:pt-0 relative z-10 transition-[margin] duration-200`}>
        <Routes>
          <Route path="/" element={<Gated featureKey="command_center"><CommandCenter user={user} onSignIn={login} /></Gated>} />
          <Route path="/connect" element={<ConnectBroker />} />
          <Route path="/ai-journal" element={<Gated featureKey="ai_journal"><AIJournal /></Gated>} />
          <Route path="/ws-ai-chart" element={<Gated featureKey="ai_chart"><WSAIChartPage /></Gated>} />
          <Route path="/ws-ai-coach" element={<Gated featureKey="ai_coach"><WSAICoach /></Gated>} />
          <Route path="/analytics" element={<Gated featureKey="analytics"><Analytics /></Gated>} />
          <Route path="/ws-news-pro" element={<Gated featureKey="news_pro"><WSNewsPro /></Gated>} />
          <Route path="/community" element={<Gated featureKey="community"><Community /></Gated>} />
          <Route path="/ws-vip" element={<Gated featureKey="ws_vip"><WSVIPCommunity /></Gated>} />
          <Route path="/growth-zone" element={<GrowthZone />} />
          <Route path="/ws-leaderboard" element={<Gated featureKey="leaderboard"><WSLeaderboard /></Gated>} />
          <Route path="/ws-ai-indicator" element={<ComingSoon feature="ws_ai_indicator" />} />
          <Route path="/ws-giveaway-zone" element={<GiveawayZone />} />
          <Route path="/ws-admin-console" element={<AdminConsole />} />
          <Route path="/disclaimer" element={<Legal doc="disclaimer" />} />
          <Route path="/privacy" element={<Legal doc="privacy" />} />
          <Route path="/terms" element={<Legal doc="terms" />} />
        </Routes>
      </main>
      <SupportWidget user={user} />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Google OAuth callback (authorization-code flow) — handle before anything else.
  if (location.pathname === "/auth/google") {
    return <GoogleCallback />;
  }
  // Legacy callback (kept harmless)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <AppShell />;
}

function App() {
  return (
    <div className="App">
      <div className="ws-app-shell">
        <div className="ws-orb a" />
        <div className="ws-orb b" />
        <BrowserRouter>
          <AuthProvider>
            <EntitlementsProvider>
              <AppRouter />
            </EntitlementsProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </div>
    </div>
  );
}

export default App;
